import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Quiz = () => {
  const navigate = useNavigate();
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);

  const questions = [
    {
      id: 1,
      number: "01",
      question: "What is the most important thing in school?",
      options: [
        { id: 'A', text: 'A. Finishing first', correct: false },
        { id: 'B', text: 'B. Doing your best', correct: true },
        { id: 'C', text: 'C. Running as fast as you can', correct: false }
      ]
    },
    {
      id: 2,
      number: "02",
      question: "Why is doing your best more important than being first?",
      options: [
        { id: 'A', text: 'A. Because it helps you learn better', correct: true },
        { id: 'B', text: 'B. Because it makes you faster', correct: false },
        { id: 'C', text: 'C. Because teachers like it more', correct: false }
      ]
    }
  ];

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const handleNext = () => {
    if (selectedAnswer) {
      const newAnswers = [...answers, selectedAnswer];
      setAnswers(newAnswers);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      } else {
        // Quiz completed, calculate score
        const correctCount = newAnswers.filter(answer => answer.correct).length;
        navigate('/results', { state: { score: correctCount, total: questions.length } });
      }
    }
  };

  const question = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-[#FBF5DE]">
      {/* Main Content */}
      <main className="px-12 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Quiz Interface */}
          <div className="bg-[#3D74B6] rounded-[40px] p-12 relative">
            {/* Background decorative image */}
            <div 
              className="absolute inset-0 rounded-[40px] opacity-30"
              style={{
                backgroundImage: `url('https://cdn.builder.io/api/v1/image/assets%2F7f7a27f2f7f847a7ad2cce53450cb7f9%2F280cc166ee324e47811795203029a6e1?format=webp&width=800')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            ></div>

            <div className="relative z-10">
              {/* Quiz Content */}
              <div className="bg-white/90 backdrop-blur-sm rounded-[20px] overflow-hidden">
                <div className="flex">
                  {/* Full Story Text */}
                  <div className="flex-1 p-12 bg-[#F2F2F2]">
                    <div className="prose max-w-none">
                      <p className="text-3xl font-['Cheese_Matcha'] text-[#000] leading-relaxed mb-8">
                        <strong>Doing Your Best is What Matters Most!</strong>
                      </p>
                      <div className="space-y-6 text-3xl font-['Cheese_Matcha'] text-[#000] leading-relaxed">
                        <p>Have you ever raced to be first in line or tried to finish a test as fast as possible? It feels exciting to win, but is being first the most important thing?</p>
                        
                        <p>Some people always want to be first. They rush to finish their work, run to the front of the line, and make sure everyone knows they finished first. But sometimes, rushing can lead to mistakes, accidents, or missing important details. If you move too fast, you might trip, forget something, or not do your best work.</p>
                        
                        <p>What really matters isn't being first—it's doing your best. Imagine a runner in a race who takes their time to practice and improve. They may not win every race, but they keep getting stronger and faster. That's what success really looks like!</p>
                        
                        <p>In school, it's not about finishing first—it's about understanding your work, learning new things, and improving each day. If you focus on being the best version of yourself, you will always be successful.</p>
                        
                        <p>Everyone moves at their own pace. Some people learn quickly, and others take more time, and that's okay! The goal is not to rush but to grow. When you take your time and work hard, you will feel proud of what you accomplish, even if you're not the first to finish.</p>
                        
                        <p>So, next time you're doing an assignment, standing in line, or playing a game, ask yourself: Am I doing my best? If the answer is yes, then you are already a winner!</p>
                      </div>
                    </div>
                  </div>

                  {/* Quiz Section */}
                  <div className="flex-1 p-12 bg-[#F2F2F2] border-l-2 border-gray-300">
                    <div className="space-y-8">
                      {/* Question Header */}
                      <div className="flex items-center gap-12">
                        <span className="text-6xl font-['Cheese_Matcha'] text-[#374957]">
                          {question.number}
                        </span>
                        <h2 className="text-4xl font-['Cheese_Matcha'] text-[#374957] flex-1 leading-normal">
                          {question.question}
                        </h2>
                      </div>

                      {/* Answer Options */}
                      <div className="space-y-6">
                        {question.options.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => handleAnswerSelect(option)}
                            className={`w-full p-6 rounded-[20px] border-2 border-[#374957] text-left transition-all ${
                              selectedAnswer?.id === option.id
                                ? 'bg-[#B4E50D] shadow-lg'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            <span className="text-3xl font-['Cheese_Matcha'] text-[#374957]">
                              {option.text}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Next Button */}
                      <div className="pt-8">
                        <button 
                          onClick={handleNext}
                          disabled={!selectedAnswer}
                          className={`w-full py-6 rounded-full text-3xl font-medium transition-all ${
                            selectedAnswer
                              ? 'gradient-primary text-[#374957] shadow-button hover:shadow-lg'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {currentQuestion < questions.length - 1 ? 'Next' : 'Finish Quiz'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="mt-8 text-center">
            <div className="inline-flex gap-4">
              {questions.map((_, index) => (
                <div 
                  key={index}
                  className={`w-4 h-4 rounded-full ${
                    index === currentQuestion 
                      ? 'bg-[#3D74B6]' 
                      : index < currentQuestion 
                        ? 'bg-[#B4E50D]' 
                        : 'bg-gray-300'
                  }`}
                ></div>
              ))}
            </div>
            <p className="mt-4 text-xl text-[#374957]">
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Quiz;
